import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { useSeoCopy } from "@/config/seo";
import { MyClubLanding } from "@/features/MyClub/Landing/MyClubLanding";
import { MyClubShell } from "@/features/MyClub/Layout/MyClubShell";
import { AppDispatch } from "@/redux/store";
import { userFavoriteClubIdsSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { fetchClubStats } from "@repo/shared/redux";
import { useClubImportFeature } from "@repo/shared/redux";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { MyClubPlayersNavigationContext } from "./Players/MyClubPlayers";

export const MyClub = () => {
  const { t } = useTranslation("myClub");
  const myClubSeo = useSeoCopy("myClub");
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  /*
    The page splits on whether a club exists, not on what the user pays: the
    import is free. `loading` is its own state so a returning importer never
    flashes the import pitch while users/profile is in flight.
  */
  const clubAccess = useClubImportFeature("see-active-squad");
  const favoriteClubCount = useSelector(userFavoriteClubIdsSelector).length;
  const hasClub = clubAccess.allowed;
  const clubAccessResolved = clubAccess.reason !== "loading";
  const isRootRoute = useMemo(
    () => location.pathname === "/my-club",
    [location.pathname],
  );
  const pathSegments = useMemo(
    () => location.pathname.split("/").filter(Boolean),
    [location.pathname],
  );

  // The layout route for every /my-club page, so this fires once per landing.
  useEffect(() => {
    window.hj?.("event", "landed-on-my-club");
  }, []);

  useEffect(() => {
    const state = location.state as { importFinished?: boolean } | undefined;
    if (state?.importFinished) dispatch(fetchClubStats());
  }, [location.state]);

  const lastSegment = useMemo(
    () => pathSegments[pathSegments.length - 1],
    [pathSegments],
  );

  // Sub-route heading: "My Players", "My Sbc Fodder Breakdown".
  const currentSection = useMemo(
    () =>
      lastSegment === "players"
        ? new URLSearchParams(location.search).get("sort") === "profit"
          ? t("section.hiddenProfit")
          : t("nav.myPlayers")
        : lastSegment === "sbc-fodder-breakdown"
          ? t("section.mySbcFodderBreakdown")
          : lastSegment === "recommendations"
            ? t("section.playerRecommendations")
            : lastSegment === "favorite-club"
              ? t(
                  favoriteClubCount > 1
                    ? "nav.favoriteClubs"
                    : "nav.favoriteClub",
                )
              : lastSegment === "favorite-country"
                ? t("nav.favoriteCountry")
                : "",
    [lastSegment, t, favoriteClubCount, location.search],
  );

  useEffect(() => {
    if (hasClub) {
      dispatch(fetchClubStats());
    }
  }, [hasClub]);

  const canonical = isRootRoute || currentSection ? canonicalUrl() : undefined;

  if (hasClub) {
    return (
      <MyClubShell
        helmetTitle={
          isRootRoute ? myClubSeo.title : `EA ${GAME_NAME} ` + currentSection
        }
        helmetDescription={isRootRoute ? myClubSeo.description : undefined}
        helmetCanonicalLink={canonical}
        sectionTitle={currentSection}
        toolbar={
          lastSegment === "players" ? <MyClubPlayersNavigationContext /> : null
        }
        fullBleed={
          lastSegment === "players" || lastSegment === "sbc-fodder-breakdown"
        }
      />
    );
  }

  /*
    No club yet: one landing, whichever My Club page was asked for. Nothing
    until access is known, so a returning importer never sees the pitch.
  */
  if (!clubAccessResolved) return null;
  return (
    <MyClubLanding
      helmetTitle={myClubSeo.title}
      helmetDescription={myClubSeo.description}
      helmetCanonicalLink={canonical}
    />
  );
};
